/**
 * Viewport navigation maths for the P&ID canvas.
 *
 * Three navigation aids share this module: edge pan (the drawing scrolls while
 * the cursor rests in a band along the canvas border), arrow-key pan, and
 * keyboard zoom. The two pan aids move the Konva stage's screen-space
 * `position`, so both are scale independent: a step covers the same number of
 * screen pixels at any zoom level.
 *
 * The functions here are pure so they can be unit tested without a browser.
 * `PidCanvas` owns the animation frame loop that drives them.
 */

export interface PanPosition {
  x: number;
  y: number;
}

/** Rate of change of `PanPosition`, in screen pixels per second. */
export interface PanVelocity {
  vx: number;
  vy: number;
}

export interface PanViewport {
  width: number;
  height: number;
}

/** Cursor position relative to the canvas container, in screen pixels. */
export interface PanPointer {
  x: number;
  y: number;
}

/** Unscaled image dimensions, in image pixels. */
export interface PanImageSize {
  width: number;
  height: number;
}

/** Width of the border band that triggers edge pan. */
export const EDGE_PAN_BAND_PX = 56;

/** Speed at the outermost pixel of the band. The ramp reaches this linearly. */
export const EDGE_PAN_MAX_SPEED_PX_PER_S = 1400;

/**
 * A frame longer than this contributes only this much travel. Without the cap a
 * backgrounded tab resumes with one multi-second frame and teleports the drawing.
 */
export const EDGE_PAN_MAX_FRAME_MS = 100;

/** How often the animation loop commits its position to React state. */
export const EDGE_PAN_COMMIT_INTERVAL_MS = 120;

/**
 * How long the cursor must rest in the band before the drawing moves.
 *
 * The canvas overlays its own controls within 10 px of three borders — the mode
 * toolbar, the zoom controls, and the instructions bar. Reaching any of them
 * means crossing the band. A cursor travelling to a button clears the band well
 * inside this delay, so only a cursor that stays there starts a pan.
 */
export const EDGE_PAN_DWELL_MS = 150;

export const ARROW_PAN_STEP_PX = 80;

/** Step used when Shift is held. */
export const ARROW_PAN_FAST_STEP_PX = 400;

/** How much of the drawing stays on screen. Panning stops at this overlap. */
export const MIN_VISIBLE_IMAGE_PX = 120;

export const MIN_CANVAS_SCALE = 0.1;
export const MAX_CANVAS_SCALE = 5;

/** Scale multiplier for one zoom-in key press. Zooming out divides by it. */
export const ZOOM_STEP_FACTOR = 1.2;

const clampToUnit = (value: number): number => Math.max(0, Math.min(1, value));

/** One axis of `clampPanPosition`. See that function for the two rules. */
const clampAxis = (
  value: number,
  scaledLength: number,
  viewportLength: number,
  minVisiblePx: number
): number => {
  const slack = viewportLength - scaledLength;
  if (slack >= 0) return Math.max(0, Math.min(value, slack));
  const visible = Math.min(minVisiblePx, viewportLength);
  return Math.max(visible - scaledLength, Math.min(value, viewportLength - visible));
};

/**
 * Speed for one axis. `lowDepth` and `highDepth` are how far the cursor has
 * travelled into the low-side and high-side bands; a value at or below zero is
 * outside that band. A cursor beyond the container edge is treated as fully
 * inside, which keeps the pan running while a drag overshoots.
 */
const axisVelocity = (
  lowDepth: number,
  highDepth: number,
  band: number,
  maxSpeed: number
): number => {
  if (band <= 0) return 0;
  if (lowDepth > 0 && lowDepth >= highDepth) {
    return clampToUnit(lowDepth / band) * maxSpeed;
  }
  if (highDepth > 0) {
    return -clampToUnit(highDepth / band) * maxSpeed;
  }
  return 0;
};

/**
 * Velocity for a cursor in the border band. Returns zero away from the border.
 * At a corner both components are non-zero, so the drawing travels diagonally.
 *
 * The sign follows the stage position, not the cursor: a cursor at the right
 * border makes `vx` negative, which slides the drawing left and reveals the
 * content to its right.
 */
export function computeEdgePanVelocity(
  pointer: PanPointer,
  viewport: PanViewport,
  bandPx: number = EDGE_PAN_BAND_PX,
  maxSpeed: number = EDGE_PAN_MAX_SPEED_PX_PER_S
): PanVelocity {
  if (!Number.isFinite(pointer.x) || !Number.isFinite(pointer.y)) return { vx: 0, vy: 0 };
  if (!(viewport.width > 0) || !(viewport.height > 0)) return { vx: 0, vy: 0 };

  // A viewport narrower than two bands would leave no neutral centre, so the
  // band shrinks to half the viewport and the two sides meet in the middle.
  const bandX = Math.min(bandPx, viewport.width / 2);
  const bandY = Math.min(bandPx, viewport.height / 2);

  return {
    vx: axisVelocity(bandX - pointer.x, pointer.x - (viewport.width - bandX), bandX, maxSpeed),
    vy: axisVelocity(bandY - pointer.y, pointer.y - (viewport.height - bandY), bandY, maxSpeed),
  };
}

/** Advance a position by one animation frame. */
export function stepPanPosition(
  position: PanPosition,
  velocity: PanVelocity,
  deltaMs: number
): PanPosition {
  const seconds = Math.max(0, Math.min(deltaMs, EDGE_PAN_MAX_FRAME_MS)) / 1000;
  return {
    x: position.x + velocity.vx * seconds,
    y: position.y + velocity.vy * seconds,
  };
}

/**
 * Hold the drawing against the viewport, on each axis independently.
 *
 * An axis where the scaled drawing is longer than the viewport keeps at least
 * `minVisiblePx` of the drawing on screen. Continuous edge pan would otherwise
 * drive the drawing off the canvas and leave the SME on an empty background
 * with no way back.
 *
 * An axis where the scaled drawing already fits keeps the drawing wholly on
 * screen. Nothing is hidden along that axis, so there is nothing to pan to, and
 * a `minVisiblePx` floor there would let edge pan evacuate a drawing the SME
 * could see in full.
 */
export function clampPanPosition(
  position: PanPosition,
  scale: number,
  viewport: PanViewport,
  imageSize: PanImageSize | null,
  minVisiblePx: number = MIN_VISIBLE_IMAGE_PX
): PanPosition {
  if (!imageSize || !(imageSize.width > 0) || !(imageSize.height > 0)) return position;
  if (!(scale > 0) || !(viewport.width > 0) || !(viewport.height > 0)) return position;

  return {
    x: clampAxis(position.x, imageSize.width * scale, viewport.width, minVisiblePx),
    y: clampAxis(position.y, imageSize.height * scale, viewport.height, minVisiblePx),
  };
}

/**
 * Position delta for one arrow key press, or null for any other key. The sign
 * matches `computeEdgePanVelocity`: ArrowRight moves the view right, so the
 * drawing itself travels left.
 */
export function arrowPanDelta(
  key: string,
  shiftKey: boolean
): { dx: number; dy: number } | null {
  const step = shiftKey ? ARROW_PAN_FAST_STEP_PX : ARROW_PAN_STEP_PX;
  switch (key) {
    case 'ArrowLeft':
      return { dx: step, dy: 0 };
    case 'ArrowRight':
      return { dx: -step, dy: 0 };
    case 'ArrowUp':
      return { dx: 0, dy: step };
    case 'ArrowDown':
      return { dx: 0, dy: -step };
    default:
      return null;
  }
}

/**
 * Zoom about a fixed point on screen: the image pixel under `anchor` stays under
 * `anchor` afterwards. The wheel anchors on the cursor and the keyboard shortcuts
 * do the same, so an SME reading one corner of a drawing zooms into that corner
 * rather than drifting toward the stage origin.
 *
 * The returned position is unclamped. Feed it to `clampPanPosition`, because a
 * zoom out can leave the drawing outside the bound that the next pan enforces.
 */
export function zoomAtPoint(
  position: PanPosition,
  scale: number,
  anchor: PanPointer,
  factor: number,
  minScale: number = MIN_CANVAS_SCALE,
  maxScale: number = MAX_CANVAS_SCALE
): { scale: number; position: PanPosition } {
  const nextScale = Math.max(minScale, Math.min(maxScale, scale * factor));
  const imagePoint = {
    x: (anchor.x - position.x) / scale,
    y: (anchor.y - position.y) / scale,
  };
  return {
    scale: nextScale,
    position: {
      x: anchor.x - imagePoint.x * nextScale,
      y: anchor.y - imagePoint.y * nextScale,
    },
  };
}

/**
 * Scale multiplier for one zoom key press, or null for any other key.
 *
 * A press carrying Cmd or Ctrl returns null on purpose. Browsers reserve those
 * combinations for their own page zoom and do not let a page cancel them, so
 * claiming them would leave the SME with a shortcut that works sometimes.
 */
export function zoomKeyFactor(
  key: string,
  modifierHeld: boolean
): number | null {
  if (modifierHeld) return null;
  switch (key) {
    case '+':
    case '=':
      return ZOOM_STEP_FACTOR;
    case '-':
    case '_':
      return 1 / ZOOM_STEP_FACTOR;
    default:
      return null;
  }
}
